import { Component, Inject, OnInit, signal } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";

import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "app/services/auth/auth.service";
import { ImagesService } from "app/services/helper/images.service";
import { PersonService } from "app/services/person/person.service";
import { ProjectService } from "app/services/project/project.service";
import { UserService } from "app/services/user/user.service";

import { Project } from "app/shared/models/project.interface";
import { Persona, User } from "app/shared/models/user.interface";
import { finalize } from "rxjs";

@Component({
    selector: "dialog-readOnly",
    templateUrl: "./dialog-readOnly.component.html",
    styleUrls: ["./dialog-readOnly.component.scss"],
    standalone: false
})
export class DialogReadOnlyComponent implements OnInit {
    readonly persons = signal<Persona[]>([]);
    readonly loadingPersons = signal(false);
    readonly switchingUser = signal(false);
    hasMorePersons: boolean;
    currentPagePersons: number;

    filterPersonValue: string;

    userId: number;
    projectId: number;
    project: Project;
    readonly personSelected = signal<Persona | null>(null);

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { badgeReturn: string; personId: number },
        private snackBar: MatSnackBar,
        private projectSvc: ProjectService,
        private imagesSvc: ImagesService,
        private userSvc: UserService,
        private personSvc: PersonService,
        private authSvc: AuthService
    ) {
        this.filterPersonValue = "";
        this.hasMorePersons = false;
        this.currentPagePersons = 0;
    }

    ngOnInit(): void {
        this.projectId = parseInt(this.projectSvc.getProjectId(), 10);
        this.userSvc.getUser().subscribe({
            next: (data: User) => {
                this.userId = data.id;
            },
            error: (err) => {
                this.showSnack(err);
            }
        });
        this.projectSvc.getProjectConfig().subscribe({
            next: (data) => {
                this.project = data;
            },
            error: (err) => {
                this.showSnack(err);
            }
        });
    }

    filterPersons(event: Event) {
        if (this.filterPersonValue.length > 2) {
            this.currentPagePersons = 0;
            this.persons.set([]);
            this.getPersons();
        } else {
            this.currentPagePersons = 0;
            this.persons.set([]);
            this.loadingPersons.set(false);
        }
    }

    getPersons() {
        this.loadingPersons.set(true);
        this.personSvc
            .getPersons(this.filterPersonValue, this.currentPagePersons, 5)
            .pipe(finalize(() => this.loadingPersons.set(false)))
            .subscribe({
                next: (personsData) => {
                    const elements = personsData?.elements ?? [];
                    this.persons.set([...elements]);
                },
                error: (err) => {
                    this.persons.set([]);
                    this.showSnack(err);
                }
            });
    }

    getPerson(personId) {
        this.personSvc.getPerson(personId).subscribe((data) => {
            this.selectPerson(data);
        });
    }

    getImagePerson(username: string) {
        return this.imagesSvc.getImageThumb(username);
    }

    getImagePersonGrande(username: string) {
        return this.imagesSvc.getImagePerson(username, this.projectId);
    }

    selectPerson(user: Persona) {
        if (this.switchingUser()) {
            return;
        }
        this.personSelected.set(user);
        this.switchingUser.set(true);
        this.userSvc.loginReadOnlyMode(user.id.toString()).subscribe({
            next: (data) => {
                this.authSvc.saveToken(data, true);
                location.reload();
            },
            error: (err) => {
                this.switchingUser.set(false);
                this.personSelected.set(null);
                this.showSnack(err);
            }
        });
    }

    clearPerson() {
        this.personSelected.set(null);
        this.filterPersonValue = "";
        this.persons.set([]);
        this.loadingPersons.set(false);
    }

    showSnack(mensaje: string) {
        this.snackBar.open(mensaje, "X", {
            duration: 3000
        });
    }
}
